import { debounce } from "lodash-es";
import { useEffect, useMemo, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useBoolean, useEventCallback, useUnmount } from "usehooks-ts";

import { GroupConfigBreakoutsBanner } from "./GroupConfigBreakoutsBanner";
import { InPersonGroupTimerButton } from "./InPersonGroupTimerButton";
import { usePageGroupOperation } from "./use-page-group-operation";

import { BreakoutDurationField } from "components/forms/BreakoutDurationField";
import { ReadOnlyBreakoutDuration } from "components/forms/ReadOnlyBreakoutDuration";
import { useCapabilities } from "store/capability-hooks";
import { store, storeApi, useAppSelector } from "store/index";
import {
  pageSelectors,
  selectActiveClassSessionId,
  selectCurrentSectionIsArchived,
  selectCurrentSectionIsLinked,
  selectIsClassSessionInPerson,
  selectIsEditModeEnabled,
  selectIsTimeline,
} from "store/selectors";
import { selectIsInBreakout } from "store/selectors/breakouts";
import { useObjectSelector } from "store/store-hooks";
import { toastLocalizedOperationCatcher } from "utils/alerts";
import { useAv } from "utils/av";
import { DEFAULT_BREAKOUT_DURATION } from "utils/breakouts";

const STEP_SAVE_DELAY_MS = 500;

export const PageBreakoutDuration = ({
  pageId,
  hasAssessments,
}: {
  pageId: string;
  hasAssessments: boolean;
}) => {
  const page = useObjectSelector((s) => pageSelectors.selectById(s, pageId), ["breakout_duration"]);
  const { canAuthorSection } = useCapabilities();
  const isLinked = useAppSelector(selectCurrentSectionIsLinked);
  const isArchived = useAppSelector(selectCurrentSectionIsArchived);
  const isTimeline = useAppSelector(selectIsTimeline);
  const isEditing = useAppSelector(selectIsEditModeEnabled);
  const activeSessionId = useAppSelector(selectActiveClassSessionId);
  const isInBreakout = useAppSelector(selectIsInBreakout);
  const isInPersonSession = useAppSelector(selectIsClassSessionInPerson);
  const { isInVirtualClass, isVirtualClassActive } = useAv();
  const canEdit =
    canAuthorSection &&
    !isLinked &&
    !isArchived &&
    !isTimeline &&
    !isInBreakout &&
    (activeSessionId && !isInPersonSession ? isInVirtualClass && isVirtualClassActive : isEditing);
  const duration = page?.breakout_duration ?? DEFAULT_BREAKOUT_DURATION;
  const form = useForm<{ breakout_duration: number | string }>({
    shouldFocusError: false,
    defaultValues: { breakout_duration: duration },
  });
  const { isPending, runPageGroupOperation } = usePageGroupOperation(pageId);
  const pendingSave = useRef<Promise<number | null> | null>(null);

  const saveRequested = useRef(false);
  const { value: isStarting, setTrue: lockDuration, setFalse: unlockDuration } = useBoolean();
  const {
    reset,
    formState: { isDirty },
  } = form;
  useEffect(() => {
    // In-flight responses must not replace edits, even a return to the original value.
    // react-doctor-disable-next-line react-doctor/no-pass-data-to-parent -- Resets this local RHF form from Redux; no parent callback.
    if (!pendingSave.current && !isDirty) reset({ breakout_duration: duration });
  }, [duration, isDirty, reset]);

  const persistDuration = useEventCallback(() => {
    if (store.getState().view.pendingPageGroupActions[pageId]) return Promise.resolve(null);
    if (!canEdit) return Promise.resolve(duration);
    let savedDuration: number | null = null;
    return form
      .handleSubmit(async ({ breakout_duration }) => {
        try {
          const value = Number(breakout_duration);
          const savedPage = pageSelectors.selectById(store.getState(), pageId);
          if (value !== (savedPage?.breakout_duration ?? DEFAULT_BREAKOUT_DURATION)) {
            // Keep the entered value retryable when the write fails.
            const saved = await runPageGroupOperation(async () => {
              await storeApi.pages.partial_update(
                { id: pageId, breakout_duration: value },
                { skipToast: true, optimistic: false },
              );
              return true;
            });
            if (!saved) return;
          }
          if (form.getValues("breakout_duration") === breakout_duration) {
            form.reset({ breakout_duration: value });
          }
          if (!store.getState().view.pendingPageGroupActions[pageId]) savedDuration = value;
        } catch (error) {
          toastLocalizedOperationCatcher("failed_to_save_breakout_duration")(error);
        }
      })()
      .then(() => savedDuration);
  });

  const saveDuration = () => {
    if (!pendingSave.current && store.getState().view.pendingPageGroupActions[pageId])
      return Promise.resolve(null);
    saveRequested.current = true;
    if (pendingSave.current) return pendingSave.current;
    // Coalesce save triggers while one write is in flight; each pass reads the latest input.
    pendingSave.current = (async () => {
      let savedDuration: number | null = null;
      while (saveRequested.current) {
        saveRequested.current = false;
        // eslint-disable-next-line no-await-in-loop -- Writes must finish in order.
        savedDuration = await persistDuration();
        if (savedDuration === null) {
          saveRequested.current = false;
          break;
        }
      }
      return savedDuration;
    })().finally(() => {
      pendingSave.current = null;
    });
    return pendingSave.current;
  };

  const saveLatestDuration = useEventCallback(saveDuration);
  const saveAfterStepping = useMemo(
    () => debounce(saveLatestDuration, STEP_SAVE_DELAY_MS),
    [saveLatestDuration],
  );
  // Cancel a pending step when its page or editing permissions change.
  useEffect(() => {
    saveAfterStepping.cancel();
    saveRequested.current = false;
  }, [saveAfterStepping, pageId, canEdit]);
  // Closing the card should persist the last step even if no blur event fires.
  useUnmount(() => {
    saveAfterStepping.flush();
  });

  const saveImmediately = () => {
    saveAfterStepping.cancel();
    return saveDuration();
  };

  const startWithSavedDuration = async (start: (value: number) => Promise<void>) => {
    lockDuration();
    try {
      const savedDuration = await saveImmediately();
      if (savedDuration !== null && !store.getState().view.pendingPageGroupActions[pageId])
        await start(savedDuration);
    } finally {
      unlockDuration();
    }
  };

  // Duration edits and Start may join this component's save, but must wait for other operations.
  const otherOperationPending = isPending && !pendingSave.current;

  return (
    <FormProvider {...form}>
      <form
        className="flex w-full flex-col gap-2"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          saveImmediately();
        }}
      >
        <GroupConfigBreakoutsBanner
          durationAction={
            <InPersonGroupTimerButton
              isDisabled={otherOperationPending}
              startWithSavedDuration={startWithSavedDuration}
            />
          }
          hasAssessments={hasAssessments}
          isDisabled={otherOperationPending}
          pageId={pageId}
          startWithSavedDuration={startWithSavedDuration}
        >
          <fieldset
            className="min-w-0"
            disabled={isStarting || otherOperationPending}
            onBlur={(event) => {
              // Start actions save themselves; a failed blur save must not be
              // retried by the same click before starting the activity.
              if (
                event.relatedTarget instanceof HTMLElement &&
                event.relatedTarget.closest("[data-saves-duration]")
              )
                return;
              if (!event.currentTarget.contains(event.relatedTarget)) saveImmediately();
            }}
            onChange={() => {
              saveAfterStepping.cancel();
              saveRequested.current = false;
            }}
          >
            {canEdit ? (
              <BreakoutDurationField
                layout="inline"
                variant="compact"
                onStep={() => {
                  saveRequested.current = false;
                  saveAfterStepping();
                }}
              />
            ) : (
              <ReadOnlyBreakoutDuration value={duration} variant="compact" />
            )}
          </fieldset>
        </GroupConfigBreakoutsBanner>
      </form>
    </FormProvider>
  );
};
